// UI theme selected at deploy time via window.__UI_THEME__ (set in public/config.js,
// loaded before the bundle). No rebuild needed to switch themes.
//
//   'minimal' (default) — flat, discreto, monocromático, pouca sombra/raio.
//   'saas'              — visual polido: cantos arredondados, sombra, áreas tingidas.
//
// Components read UI_THEME once and pick their classes via `themed({ minimal, saas })`.

export type UiTheme = 'minimal' | 'saas';
export type ColorMode = 'light' | 'dark' | 'system';

declare global {
  interface Window {
    __UI_THEME__?: string;
    __COLOR_MODE__?: string;
  }
}

const resolve = (): UiTheme => {
  const v = typeof window !== 'undefined' ? window.__UI_THEME__ : undefined;
  return v === 'saas' ? 'saas' : 'minimal';
};

export const UI_THEME: UiTheme = resolve();

const resolveColorMode = (): ColorMode => {
  const v = typeof window !== 'undefined' ? window.__COLOR_MODE__ : undefined;
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
};

export const COLOR_MODE: ColorMode = resolveColorMode();

const shouldUseDarkMode = (mode: ColorMode): boolean => {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
};

export const applyColorMode = (mode: ColorMode = COLOR_MODE): void => {
  if (typeof document === 'undefined') return;

  const isDark = shouldUseDarkMode(mode);
  document.documentElement.classList.toggle('dark', isDark);
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
};

applyColorMode();

if (typeof window !== 'undefined' && COLOR_MODE === 'system') {
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', () => applyColorMode('system'));
}

/** Picks the value for the active theme. `themed({ minimal: 'a', saas: 'b' })`. */
export const themed = <T,>(variants: Record<UiTheme, T>): T => variants[UI_THEME];
